/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button, FlexColumn, FlexRow, Typography } from 'design-system';
import { isSameId } from '../surveyPreviewUtils';

function readEmployeesFromStorage() {
  try {
    const raw = localStorage.getItem('employees');
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Employee picker for survey inputs (used by L&D preview flow).
 * Reads staff from localStorage.employees (populated when a location is available).
 * Client Connect continues to filter out employees questions before render.
 */
export default function EmployeeOptions({
  preview = false,
  value,
  onChange,
  interactive = false,
  loading = false,
}) {
  const intl = useIntl();
  const [employees, setEmployees] = useState(() => readEmployeesFromStorage());

  useEffect(() => {
    setEmployees(readEmployeesFromStorage());
  }, [loading]);

  if (loading) {
    return (
      <Typography component="p" variant="bodyMedium" color="ui-subtle">
        {intl.formatMessage({ id: 'getting-employee' })}
      </Typography>
    );
  }

  if (!employees.length) {
    return (
      <Typography component="p" variant="bodyMedium" color="ui-subtle">
        {intl.formatMessage({ id: 'no-employees-found' })}
      </Typography>
    );
  }

  return (
    <FlexRow
      wrap
      align="start"
      justify="start"
      fullWidth
      gap="var(--ds-space-4)"
      className={preview ? 'cc-preview-employee-list' : ''}
      role="group"
    >
      {employees.map((employee) => {
        const employeeId = employee?.id ?? employee?._id;
        const selected = isSameId(value, employeeId);

        return (
          <Button
            key={String(employeeId)}
            type="button"
            variant="link"
            className={[
              preview ? 'cc-preview-choice-option' : 'radio-option',
              selected ? 'cc-preview-choice-option--selected' : '',
              'cc-preview-employee-option',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={selected}
            disabled={!interactive}
            onClick={() => interactive && onChange?.(employeeId)}
            style={{
              width: preview ? '140px' : '150px',
              padding: 'var(--ds-space-3)',
              border: selected
                ? '1px solid var(--ds-color-border-selected, #94a3b8)'
                : '1px solid var(--ds-color-border, #64748b)',
              borderRadius: '5px',
              backgroundColor: selected ? 'var(--pal-grey-blue-100, #e2e8f0)' : 'transparent',
            }}
          >
            <FlexColumn align="center" fullWidth gap="var(--ds-space-2)">
              {employee?.image ? (
                <img
                  src={employee.image}
                  alt=""
                  style={{
                    width: '100%',
                    maxWidth: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                  }}
                />
              ) : null}
              <Typography component="span" variant="bodyMedium" style={{ textAlign: 'center' }}>
                {employee?.name || ''}
              </Typography>
            </FlexColumn>
          </Button>
        );
      })}
    </FlexRow>
  );
}

EmployeeOptions.propTypes = {
  preview: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  interactive: PropTypes.bool,
  loading: PropTypes.bool,
};
